import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import { Rocket, Calendar, Gift, Lock, Loader2, LayoutGrid, Menu, X } from 'lucide-react'; // 🔴 تمت إضافة Menu و X
import SubscriptionModal from './SubscriptionModal';
import Footer from './Footer';
import './PricingCard.css';

const PricingCard = ({ darkMode }) => {
  const navigate = useNavigate();

  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [deviceFingerprint, setDeviceFingerprint] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [errorMessages, setErrorMessages] = useState({});
  const [filter, setFilter] = useState("lecture"); 
  const [subscribedPackageIds, setSubscribedPackageIds] = useState([]);

  // 🔴 التحكم في قائمة الموبايل
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // 🔴 مراقبة حجم الشاشة بشكل حي
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ====== جلب البيانات ======
  const fetchData = async () => {
    setLoading(true);
    try {
      const educationalLevelId = localStorage.getItem('educationalLevelId');
      const authToken = localStorage.getItem('authToken');
      const Teacher_id = localStorage.getItem("teacher_id");

      if (!authToken || !educationalLevelId) {
        navigate('/Signin');
        return;
      }

      const packagesPromise = axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/getPackages/${Teacher_id}/${educationalLevelId}`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      const subscriptionsPromise = axios.get(
        'https://leader-acadmy.hwnix.com/api/user/subscriptions',
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      const [packagesRes, subsRes] = await Promise.all([packagesPromise, subscriptionsPromise]);

      setLessons(packagesRes.data.packages || []);

      if (subsRes.data && subsRes.data.status && subsRes.data.packages) {
        const subIds = subsRes.data.packages.map(pkg => pkg.id);
        setSubscribedPackageIds(subIds);
      }

    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchFingerprint = async () => {
      try {
        const fp = await FingerprintJS.load();
        const result = await fp.get();
        setDeviceFingerprint(result.visitorId);
        localStorage.setItem('deviceFingerprint', result.visitorId);
      } catch (err) {
        navigate('/login');
      }
    };
    fetchFingerprint();
    fetchData(); 
  }, [navigate]);

  // ====== منطق الاشتراك والدخول ======
  const handleSubscribeClick = lesson => {
    localStorage.setItem('package_id', lesson.id);
    setSelectedLesson(lesson);
    setIsModalOpen(true);
  };

  const handleEnterClick = async (lesson) => {
    if (!lesson) return; 
    localStorage.setItem('package_id', lesson.id);
    const id = localStorage.getItem('id');
    const subscriptionCode = localStorage.getItem('subscriptionCode');
    const deviceFingerprint = localStorage.getItem('deviceFingerprint');

    try {
      const response = await axios.get(
        `https://leader-acadmy.hwnix.com/api/code/check/${id}/${deviceFingerprint}/${lesson.id}?code=${subscriptionCode}`
      );

      if (response.data.message === 'User has a valid code.') {
        navigate('/ProfileCard');
      } else {
        setErrorMessages(prev => ({ ...prev, [lesson.id]: 'الكود غير صالح أو يوجد مشكلة.' }));
      }
    } catch (err) {
      const msg = err.response?.data?.message;
      let errorText = 'من فضلك ادخل كود الاشتراك أولاً';
      if (msg === 'MAC address mismatch.') errorText = 'برجاء الدخول من الجهاز الأصلي';
      setErrorMessages(prev => ({ ...prev, [lesson.id]: errorText }));
    }
  };

  const handleCardTerm = async (lesson) => {
    if (!lesson) return; 
    localStorage.setItem('package_id', lesson.id);
    const id = localStorage.getItem('id');
    const subscriptionCode = localStorage.getItem('subscriptionCode');
    const deviceFingerprint = localStorage.getItem('deviceFingerprint');

    try {
      const response = await axios.get(
        `https://leader-acadmy.hwnix.com/api/code/check/${id}/${deviceFingerprint}/${lesson.id}?code=${subscriptionCode}`
      );

      if (response.data.message === 'User has a valid code.') {
        navigate('/CardTerm');
      } else {
        setErrorMessages(prev => ({ ...prev, [lesson.id]: 'الكود غير صالح أو يوجد مشكلة.' }));
      }
    } catch (err) {
      const msg = err.response?.data?.message;
      let errorText = 'من فضلك ادخل كود الاشتراك أولاً';
      if (msg === 'MAC address mismatch.') errorText = 'برجاء الدخول من الجهاز الأصلي';
      setErrorMessages(prev => ({ ...prev, [lesson.id]: errorText }));
    }
  };

  useEffect(() => {
    const handleContextMenu = e => e.preventDefault();
    document.addEventListener('contextmenu', handleContextMenu);
    return () => document.removeEventListener('contextmenu', handleContextMenu);
  }, []);

  const filteredLessons = lessons.filter(lesson => {
    if (!filter) return false;
    const title = lesson.title.toLowerCase();
    if (filter === "month") return title.includes("شهر");
    if (filter === "lecture") return title.includes("محاضر");
    if (filter === "offer") return title.includes("عرض");
    return false;
  });

  // 🔴 دالة لتجميع أزرار الفلترة عشان نستخدمها مرتين (للموبايل والديسكتوب)
  const renderNavButtons = () => (
    <>
      <button className={`nav-pill ${filter === "month" ? "active" : ""}`} onClick={() => { setFilter("month"); setIsMenuOpen(false); }}>
        <Calendar size={18} /> <span>الشهور</span>
      </button>
      <button className={`nav-pill ${filter === "lecture" ? "active" : ""}`} onClick={() => { setFilter("lecture"); setIsMenuOpen(false); }}>
        <Rocket size={18} /> <span>المحاضرات</span>
      </button>
      <button className={`nav-pill ${filter === "offer" ? "active" : ""}`} onClick={() => { setFilter("offer"); setIsMenuOpen(false); }}>
        <Gift size={18} /> <span>العروض</span>
      </button>
      <button className="nav-pill btn-my-subs" onClick={() => { navigate("/MySubscriptions"); setIsMenuOpen(false); }}>
        <LayoutGrid size={18} /> <span>اشتراكاتي</span>
      </button>
    </>
  );

  return (
    <div className={`pricing-page-master ${darkMode ? 'dark-mode' : ''}`}>
      
      {/* 🔴 قسم الفلتر الجديد (يدعم الموبايل والديسكتوب) */}
      <div className="filter-nav-bar">
        <div className="filter-inner">
          {isMobile ? (
            <div className="mobile-menu-container">
              <button className="mobile-menu-btn" onClick={() => setIsMenuOpen(!isMenuOpen)}>
                {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
                <span>القائمة</span>
              </button>
              
              <AnimatePresence>
                {isMenuOpen && (
                  <motion.div 
                    className="mobile-dropdown"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                  >
                    {renderNavButtons()}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ) : (
            <div className="desktop-nav-buttons">
              {renderNavButtons()}
            </div>
          )}
        </div>
      </div>

      <div className="container-main">
        {loading ? (
          <div className="loader-center">
            <Loader2 className="spinner-icon" size={48} />
            <p>جاري تحضير المحتوى...</p>
          </div>
        ) : (
          <div className="content-grid-layout">
            <AnimatePresence mode="wait">
              <motion.div
                key={filter}
                className="cards-3-column-grid"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
              >
                {filteredLessons.map((lesson) => {
                  const isSubscribed = subscribedPackageIds.includes(lesson.id);

                  return (
                    <div key={lesson.id} className="modern-lesson-card">
                      <div className="card-img-holder">
                        <img
                          src={`https://leader-acadmy.hwnix.com/storage/${lesson.image?.image_url}`}
                          alt={lesson.title || 'صورة الدرس'}
                        />
                        <div className="glass-tag">{filter === 'month' ? 'باقة شهرية' : 'محاضرة'}</div>
                      </div>

                      <div className="card-content-box">
                        <h3>{lesson.title}</h3>
                        <p className="lesson-desc">{lesson.description}</p>

                        {isSubscribed && (
                          <span className="subscribed-badge">✓ تم الاشتراك</span>
                        )}

                        {errorMessages[lesson.id] && (
                          <div className="card-alert"><Lock size={14} /> {errorMessages[lesson.id]}</div>
                        )}
                      </div>

                      <div className="card-footer-actions">
                        <button className="btn-enter" onClick={() => handleEnterClick(lesson)}>دخول</button>
                        {!isSubscribed && (
                          <button className="btn-sub" onClick={() => handleSubscribeClick(lesson)}>اشتراك</button>
                        )}
                      </div>
                    </div>
                  );
                })}

                {/* كارت باقة الترم الثابت */}
                <div className="modern-lesson-card term-special-card">
                  <div className="card-img-holder">
                    <img src="/images/saas.png" alt="باقة الترم" />
                    <div className="glass-tag blue">باقة الترم</div>
                  </div>
                  <div className="card-content-box">
                    <h3>باقة الترم الكاملة</h3>
                    <p className="lesson-desc">احصل على كافة محاضرات الترم بخصم خاص</p>
                  </div>
                  <div className="card-footer-actions">
                    <button className="btn-enter" onClick={() => handleCardTerm(lessons)}>دخول</button>
                    <button className="btn-sub" onClick={() => handleSubscribeClick(lessons)}>اشتراك</button>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        )}
      </div>

      {isModalOpen && (
        <SubscriptionModal
          lesson={selectedLesson}
          onClose={() => setIsModalOpen(false)}
        />
      )}
    </div>
  );
};

export default PricingCard;